import { useEffect } from 'react';
import { trackEvent, trackBeginCheckout, trackOutboundClick } from './analytics';

// Product slug in a Spiffy checkout URL: secure.tradingindicators.com/checkout/<slug>
const CHECKOUT_RE = /secure\.tradingindicators\.com\/checkout\/([a-z0-9-]+)/i;

// Collect data-ti-* attributes off an element into a params object, dropping the
// data-ti- prefix and converting the rest kebab → snake (data-ti-cta-location →
// cta_location). data-ti-track itself is the event name, not a param.
function collectParams(el) {
  const params = {};
  for (const attr of Array.from(el.attributes)) {
    if (attr.name.startsWith('data-ti-') && attr.name !== 'data-ti-track') {
      params[attr.name.slice('data-ti-'.length).replace(/-/g, '_')] = attr.value;
    }
  }
  return params;
}

// Site-wide delegated click tracking. Mount once (in App). Runs in the CAPTURE
// phase so the event is recorded even when a handler navigates away or calls
// stopPropagation. Three jobs, in order:
//   1. any element tagged [data-ti-track] → a named GA event with its data-ti-* params
//   2. checkout links → begin_checkout (the money path), slug parsed from the URL
//   3. any other cross-host link → outbound_click
export function useTiClickTracking() {
  useEffect(() => {
    function onClick(e) {
      const target = e.target;
      if (!target || typeof target.closest !== 'function') return;

      // 1. Explicitly annotated checkpoints.
      const tracked = target.closest('[data-ti-track]');
      if (tracked) {
        trackEvent(tracked.getAttribute('data-ti-track'), collectParams(tracked));
      }

      // 2 & 3. Link-based events.
      const anchor = target.closest('a[href]');
      if (!anchor) return;
      const href = anchor.getAttribute('href') || '';

      const checkout = href.match(CHECKOUT_RE);
      if (checkout) {
        trackBeginCheckout({
          item_id: checkout[1],
          item_name: anchor.getAttribute('data-ti-item') || checkout[1],
          variant: anchor.getAttribute('data-ti-variant') || undefined,
          value: anchor.getAttribute('data-ti-value') || undefined,
          destination: href,
        });
        return;
      }

      try {
        const url = new URL(href, window.location.href);
        if (/^https?:$/.test(url.protocol) && url.host !== window.location.host) {
          trackOutboundClick({ destination: url.href, label: (anchor.textContent || '').trim() });
        }
      } catch {
        /* non-navigable href (#, mailto:, tel:) — ignore */
      }
    }

    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, []);
}
